import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { CreatorSalesTable } from './creator-sales-table'
import { CreatorPayoutsTable } from './creator-payouts-table'
import { useCreatorSalesSummary } from '@/hooks/use-creators'
import { Skeleton } from '@/components/ui/skeleton'

interface CreatorSalesTabProps {
  creatorId: string
}

/**
 * Renders a dashboard tab showing a creator's sales summary, commissions, and detailed history tables.
 *
 * @param creatorId - Identifier of the creator whose sales and payout data will be displayed
 * @returns A React element containing summary metric cards (total sales, revenue, approved and pending commissions) and two history tables (payouts and sales) for the specified creator
 */
export function CreatorSalesTab({ creatorId }: CreatorSalesTabProps) {
  const { data: summaryResult, isLoading: isLoadingSummary } = useCreatorSalesSummary(creatorId)

  // BaseApiClient automatically returns the parsed JSON. The actual data is usually inside `.data`
  const summary =
    (summaryResult as { data?: Record<string, number> })?.data ||
    (summaryResult as Record<string, number>)

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {isLoadingSummary ? (
          <>
            <Skeleton className="h-[120px] w-full" />
            <Skeleton className="h-[120px] w-full" />
            <Skeleton className="h-[120px] w-full" />
            <Skeleton className="h-[120px] w-full" />
          </>
        ) : (
          <>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                <CardTitle className="text-sm font-medium">Total de Vendas</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{summary?.totalSales || 0}</div>
                <p className="text-xs text-muted-foreground mt-1">Pedidos gerados via indicação</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                <CardTitle className="text-sm font-medium">Receita Gerada</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {new Intl.NumberFormat('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  }).format(summary?.totalSalesAmount || 0)}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                <CardTitle className="text-sm font-medium">Comissões Aprovadas</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600 dark:text-green-500">
                  {new Intl.NumberFormat('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  }).format(summary?.approvedCommission || 0)}
                </div>
                <p className="text-xs text-muted-foreground mt-1">Saldo disponível para payout</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                <CardTitle className="text-sm font-medium">Comissões Pendentes</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-500">
                  {new Intl.NumberFormat('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  }).format(summary?.pendingCommission || 0)}
                </div>
                <p className="text-xs text-muted-foreground mt-1">Dentro da garantia</p>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      <div className="grid gap-6 md:grid-cols-1">
        <Card>
          <CardHeader>
            <CardTitle>Histórico de Payouts</CardTitle>
            <CardDescription>
              Acompanhe os fechamentos mensais e pagamentos realizados para este creator.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <CreatorPayoutsTable creatorId={creatorId} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Histórico de Vendas</CardTitle>
            <CardDescription>
              Todas as transações e clientes atribuídos ao afiliado.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <CreatorSalesTable creatorId={creatorId} />
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
